import { useEffect, useState } from 'react';
import { useAppStore } from '@/store/appStore';
import { useAuthStore } from '@/store/authStore';
import { Clock, FolderKanban, Users, TrendingUp, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const formatTime = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { 
    currentOrg, 
    projects, 
    fetchProjects, 
    createProject,
    createOrganization,
    organizations,
    fetchOrganizations
  } = useAppStore();
  
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDesc, setProjectDesc] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgDesc, setOrgDesc] = useState('');
  const [stats, setStats] = useState({ totalTime: 0, tasksCompleted: 0, activeProjects: 0 });

  useEffect(() => {
    if (currentOrg) {
      fetchProjects(currentOrg.id);
    }
  }, [currentOrg, fetchProjects]);

  useEffect(() => {
    setStats({
      totalTime: projects.reduce((acc, p) => acc + (p.total_time || 0), 0),
      tasksCompleted: 0,
      activeProjects: projects.length
    });
  }, [projects]);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!currentOrg) {
      toast.error('Please select or create an organization first');
      return;
    }
    try {
      const project = await createProject(projectName, projectDesc, currentOrg.id);
      toast.success('Project created!');
      setIsProjectDialogOpen(false);
      setProjectName('');
      setProjectDesc('');
      navigate(`/projects/${project.id}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    try {
      await createOrganization(orgName, orgDesc);
      toast.success('Organization created!');
      setIsOrgDialogOpen(false);
      setOrgName('');
      setOrgDesc('');
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="dashboard-page">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white font-['Manrope'] mb-2">
          Welcome back, {user?.name?.split(' ')[0] || 'there'}!
        </h1>
        <p className="text-zinc-400">
          {currentOrg ? `Managing ${currentOrg.name}` : 'Get started by creating an organization'}
        </p>
      </div>

      {/* No Org State */}
      {organizations.length === 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center mb-8">
          <FolderKanban className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Create your first organization</h3>
          <p className="text-zinc-400 mb-4">Organizations help you manage teams and projects</p>
          <Dialog open={isOrgDialogOpen} onOpenChange={setIsOrgDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-cyan-500 text-black hover:bg-cyan-400" data-testid="create-first-org-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Organization
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white">Create Organization</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateOrg} className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1.5">Name</label>
                  <Input
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Acme Inc."
                    className="bg-zinc-950 border-zinc-800"
                    required
                    data-testid="org-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1.5">Description</label>
                  <Textarea
                    value={orgDesc}
                    onChange={(e) => setOrgDesc(e.target.value)}
                    placeholder="Brief description..."
                    className="bg-zinc-950 border-zinc-800"
                    data-testid="org-desc-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-cyan-500 text-black hover:bg-cyan-400" data-testid="create-org-submit-btn">
                  Create Organization
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Stats Grid */}
      {currentOrg && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="stat-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-cyan-400" />
                </div>
                <span className="text-zinc-400 text-sm">Time Tracked Today</span>
              </div>
              <p className="text-3xl font-bold text-white font-['JetBrains_Mono']" data-testid="time-tracked-stat">
                {formatTime(stats.totalTime)}
              </p>
            </div>

            <div className="stat-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-zinc-400 text-sm">Tasks Completed</span>
              </div>
              <p className="text-3xl font-bold text-white font-['JetBrains_Mono']" data-testid="tasks-completed-stat">
                {stats.tasksCompleted}
              </p>
            </div>

            <div className="stat-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-violet-500/10 rounded-lg flex items-center justify-center">
                  <FolderKanban className="w-5 h-5 text-violet-400" />
                </div>
                <span className="text-zinc-400 text-sm">Active Projects</span>
              </div>
              <p className="text-3xl font-bold text-white font-['JetBrains_Mono']" data-testid="active-projects-stat">
                {stats.activeProjects}
              </p>
            </div>
          </div>

          {/* Projects Section */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Projects</h2>
            <Dialog open={isProjectDialogOpen} onOpenChange={setIsProjectDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-zinc-800 hover:bg-zinc-700 text-white" data-testid="create-project-btn">
                  <Plus className="w-4 h-4 mr-2" /> New Project
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-white">Create Project</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">Project Name</label>
                    <Input
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      placeholder="Website Redesign"
                      className="bg-zinc-950 border-zinc-800"
                      required
                      data-testid="project-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">Description</label>
                    <Textarea
                      value={projectDesc}
                      onChange={(e) => setProjectDesc(e.target.value)}
                      placeholder="Brief description..."
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="project-desc-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-cyan-500 text-black hover:bg-cyan-400" data-testid="create-project-submit-btn">
                    Create Project
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {projects.length === 0 ? (
            <div className="bg-zinc-900/50 border border-zinc-800 border-dashed rounded-xl p-12 text-center">
              <FolderKanban className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No projects yet</h3>
              <p className="text-zinc-400 mb-4">Create your first project to start tracking tasks</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 cursor-pointer hover:border-zinc-700 hover:-translate-y-1 transition-all group"
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-violet-500 rounded-lg flex items-center justify-center">
                      <FolderKanban className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded">
                      {project.members?.length || 1} member{(project.members?.length || 1) !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-cyan-400 transition-colors">
                    {project.name}
                  </h3>
                  <p className="text-sm text-zinc-400 line-clamp-2">
                    {project.description || 'No description'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
